import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SiteHead from "@/components/SiteHead";
import ChatWidget from "@/components/ChatWidget";
import {
  LandingHeader, LandingFooter, LandingStickyCta,
  landingLogoVars, yearlyPlans,
} from "@/components/LandingChrome";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { BRAND_HANDLE } from "@/lib/brand";
import {
  Search, Heart, Ticket, Percent, Package, Sparkles,
  ShoppingCart, UserPlus, IndianRupee, ArrowRight,
  Smartphone, Truck, ClipboardList,
} from "lucide-react";

const FEATURES = [
  { icon: ShoppingCart, title: "Cart & checkout", body: "COD, UPI, Razorpay, Cashfree and WhatsApp orders." },
  { icon: IndianRupee, title: "Desi payments", body: "Take money the way Indian shoppers actually pay." },
  { icon: Search, title: "SEO built-in", body: "Sitemaps, meta tags and Google-ready store pages." },
  { icon: Truck, title: "India shipping zones", body: "Charge by state or 6-digit pincode." },
  { icon: UserPlus, title: "Customer accounts", body: "Login, order history and wallet out of the box." },
  { icon: Percent, title: "Discounts & coupons", body: "Flat or percentage offers with limits." },
  { icon: Heart, title: "Wishlist", body: "Let shoppers save what they love." },
  { icon: Ticket, title: "GST billing", body: "INR pricing with Indian tax invoices." },
  { icon: Package, title: "Catalogs that grow", body: "Products, variants, media and pages." },
  { icon: ClipboardList, title: "Order management", body: "Track, update and fulfil from one dashboard." },
  { icon: Smartphone, title: "Your own Android app", body: "One-click signed build plus Play Store help." },
  { icon: Sparkles, title: "Themes", body: "Storefront designs made for Indian brands." },
];

export default function FeaturesPage() {
  const cfg = useSiteConfig();
  const websitePrice = yearlyPlans(cfg.pricing?.plans)[0]?.price ?? 1999;
  return (
    <div className="ms-landing min-h-screen font-sans antialiased" style={landingLogoVars(cfg)}>
      <SiteHead
        path="/features"
        title={`${BRAND_HANDLE} features — cart, COD, UPI, SEO`}
        description={`${BRAND_HANDLE} includes cart, UPI, COD, GST billing, India shipping zones, themes, WhatsApp checkout and an optional Android app.`}
      />
      <LandingHeader websitePrice={websitePrice} active="features" />
      <main className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12 sm:py-16">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">{BRAND_HANDLE}</p>
        <h1 className="mt-2 font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-stone-900" data-testid="features-h1">
          {BRAND_HANDLE} features
        </h1>
        <p className="mt-4 text-lg text-stone-600 max-w-2xl">
          Everything an Indian dukaan needs to sell online — without a Shopify-sized bill.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-black/10 bg-white p-5">
                <Icon className="w-5 h-5 text-[#E85D04]" />
                <h2 className="mt-3 font-bold text-stone-900">{f.title}</h2>
                <p className="mt-1 text-sm text-stone-600">{f.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/signup?plan=website">
            <Button className="h-11 px-6 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-semibold">
              Launch store <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <Link to="/pricing" className="inline-flex items-center font-semibold text-stone-700 hover:text-stone-900">See pricing</Link>
        </div>
      </main>
      <LandingFooter />
      <LandingStickyCta websitePrice={websitePrice} />
      <ChatWidget />
    </div>
  );
}
